import { token } from '@atlaskit/tokens';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import EditFilledIcon from '@atlaskit/icon/core/edit';
import TrashIcon from '@atlaskit/icon/core/delete';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import { ModalTransition } from '@atlaskit/modal-dialog';
import traceUFOPress from '@atlassian/react-ufo/trace-press';

import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import config from 'appconfig';
import AmountWithCurrency from 'components/common/AmountWithCurrency';
import ButtonLink, { AnchorLink } from 'components/common/ButtonLink';
import { Dimensions } from 'components/visuals';
import Tooltip from 'components/common/Tooltip';
import { State } from 'model/State';
import t from 'modules/i18n/intl';
import { ORDER_INVOICE_ACTION_PDF_UFO_NAME } from 'util/ufoNameConstants';

import DeleteQuoteModal from './DeleteQuoteModal';
import {
  fetchCartInvoicePDF,
  fetchOrderPDF,
  openPONumberUpdateModal,
  cloneQuote,
  fetchQuotesPost,
  copyCartForEditing,
  CART_CLEAR_CLONE_QUOTE,
  fetchOrderJSON,
} from '../duck/actions/orders';
import { selectClonedOrderResponse, selectCopiedCartUUIDForEditing } from '../duck/selectors';
import { messages } from '../messages';
import { removeTimezone } from '../common/utils';
import { StyledParagraphWithTextAlign, StyledSpanWithLineHeight } from './styledComponents';

const Cell = styled.td<{ width: number; $color?: string }>`
  font-size: 14px;
  align-self: center;
  width: ${({ width }) => width}%;
  padding: ${Dimensions.units(1)}px 0;

  && span {
    line-height: unset;
  }
  ${({ $color }) => $color && `color: ${$color}`};
`;

const ChevronCellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const OrderNumberWrapper = styled.span`
  white-space: normal;
`;

const OrdersActionsCell = styled(Cell)`
  display: flex;
  align-items: center;
  gap: ${Dimensions.units(1)}px;
  width: 100%;

  span {
    display: inline-flex;
    align-self: center;
  }
`;

const StyledBoldStrong = styled.strong`
  font-weight: 600;
`;

const getOrderStatus = (
  { ccpQuoteMigrationStatus, status, ccpQuoteId }: any,
  isPartner?: boolean,
) => {
  if (ccpQuoteMigrationStatus === 'inprogress') {
    return t('mac.orders.table.table-item.status.ccp-migration');
  }

  if (ccpQuoteMigrationStatus === 'completed' && status === 'DELETED') {
    if (isPartner) {
      return t('mac.orders.table.table-item.status.ppc-migration', { ccpQuoteId });
    }
    return t('mac.orders.table.table-item.status.migration', { ccpQuoteId });
  }

  if (status === 'REVIEW_REQUIRED') return 'UNDER REVIEW';
  if (status === 'QUOTED') return '';

  return 'N/A';
};

type OrdersActionLinkType = {
  label: any;
  href?: string;
  disabled?: boolean;
  onClick?: (e: React.BaseSyntheticEvent) => void;
  testId?: string;
};

const getCCPMigrationActions = (rowItem: any, isPartner?: boolean) => {
  const { ccpQuoteTxaId, ccpQuoteUuid, ccpQuoteMigrationStatus, cartOrderDetails } = rowItem;
  const links: OrdersActionLinkType[] = [];
  if (
    ccpQuoteMigrationStatus !== 'inprogress' &&
    (cartOrderDetails.currency !== 'AUD' ||
      cartOrderDetails.partner?.organisationDetails?.payOnAccount)
  ) {
    if (isPartner) {
      links.push({
        label: t('mac.orders.table.table-item.action.pay'),
        href: `${config.ppcUrl}/account/${ccpQuoteTxaId}/quotes/${ccpQuoteUuid}`,
      });
    }

    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      href: `${config.adminHubUrl}/billing/${ccpQuoteTxaId}/quotes/${ccpQuoteUuid}`,
    });
  } else {
    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      disabled: true,
    });
  }

  return links;
};

const getQuotedCartActions = (
  rowItem: any,
  handleToggleDeleteModal: (e: React.BaseSyntheticEvent) => void,
  handleJSONDownload: (e: React.BaseSyntheticEvent, orderId: string) => void,
  handleInvoiceDownload: (e: React.BaseSyntheticEvent, cartId: string) => void,
  handlePDFDownload: (e: React.BaseSyntheticEvent, orderId: string) => void,
  handleUpdatePONumber: (e: React.BaseSyntheticEvent, rowItem: any) => void,
  handleCloneQuote: (e: React.BaseSyntheticEvent, cartId: string) => void,
  handleEditQuote: (e: React.BaseSyntheticEvent, uuid: string) => void,
  isPartner?: boolean,
  urlOrderId?: string,
  hash?: string,
) => {
  const {
    orderId,
    cloneable,
    ccpQuoteMigrationStatus,
    partner,
    cartOrderDetails,
    uuid,
    status,
    isEditableByCustomer,
    id,
  } = rowItem;

  const links: OrdersActionLinkType[] = [];

  const shouldShowEditAction = urlOrderId && hash ? !!cartOrderDetails && orderId : true;

  if (status === 'QUOTED' && isPartner) {
    links.push({
      label: t('mac.orders.table.table-item.action.clone'),
      onClick: (e) => handleCloneQuote(e, id),
      disabled: !cloneable,
      testId: 'cloneLink',
    });
  }

  const pdfLink = {
    label: 'PDF',
    onClick: (e) => handlePDFDownload(e, orderId),
  };

  const jsonLink = {
    label: 'JSON',
    onClick: (e) => handleJSONDownload(e, orderId),
  };

  const invoiceLink = {
    label: t('mac.orders.table.table-item.action.invoice'),
    onClick: (e) => handleInvoiceDownload(e, id),
    testId: 'invoiceLink',
  };

  links.push(pdfLink, jsonLink, invoiceLink);

  if (
    ccpQuoteMigrationStatus !== 'inprogress' &&
    (cartOrderDetails.currency !== 'AUD' || partner?.organisationDetails?.payOnAccount)
  ) {
    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      href: `/purchase/checkout/payment?uuid=${uuid}`,
      testId: 'payLink',
    });
  } else {
    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      disabled: true,
      testId: 'payLink',
    });
  }

  links.push({
    label: t('mac.orders.table.table-item.action.update-po'),
    onClick: (e) => handleUpdatePONumber(e, rowItem),
    testId: 'updatePOLink',
  });

  if (status !== 'DELETED' && shouldShowEditAction) {
    links.push({
      label: (
        <EditFilledIcon
          label={t('mac.orders.table.table-item.action.edit') as string}
          size="medium"
        />
      ),
      onClick: (e) => handleEditQuote(e, uuid),
      disabled: !isEditableByCustomer,
      testId: 'editQuote',
    });
  }

  if (!hash) {
    links.push({
      label: (
        <TrashIcon label={t('mac.orders.table.table-item.action.delete') as string} size="medium" />
      ),
      onClick: handleToggleDeleteModal,
      testId: 'deleteQuote',
    });
  }
  return links;
};

const getOrderActions = (
  rowItem: any,
  handleToggleDeleteModal: (e: React.BaseSyntheticEvent) => void,
  handleJSONDownload: (e: React.BaseSyntheticEvent, orderId: string) => void,
  handleInvoiceDownload: (e: React.BaseSyntheticEvent, cartId: string) => void,
  handlePDFDownload: (e: React.BaseSyntheticEvent, orderId: string) => void,
  handleUpdatePONumber: (e: React.BaseSyntheticEvent, rowItem: any) => void,
  handleCloneQuote: (e: React.BaseSyntheticEvent, cartId: string) => void,
  handleEditQuote: (e: React.BaseSyntheticEvent, cartId: string) => void,
  isPartner?: boolean,
  urlOrderId?: string,
  hash?: string,
): OrdersActionLinkType[] => {
  const { status, ccpQuoteMigrationStatus } = rowItem;

  if (ccpQuoteMigrationStatus === 'inprogress' || ccpQuoteMigrationStatus === 'completed') {
    return getCCPMigrationActions(rowItem, isPartner);
  }
  if (status === 'QUOTED') {
    return getQuotedCartActions(
      rowItem,
      handleToggleDeleteModal,
      handleJSONDownload,
      handleInvoiceDownload,
      handlePDFDownload,
      handleUpdatePONumber,
      handleCloneQuote,
      handleEditQuote,
      isPartner,
      urlOrderId,
      hash,
    );
  }
  return [
    {
      label: 'JSON',
      onClick: (e) => handleJSONDownload(e, rowItem.orderId),
    },
  ];
};

const ReviewRequiredTooltip = () => (
  <Tooltip
    direction="up-middle"
    content={
      <StyledParagraphWithTextAlign $textAlign="center">
        <FormattedMessage {...messages.quoteUnderReviewTooltip} />
      </StyledParagraphWithTextAlign>
    }
  >
    <StyledSpanWithLineHeight
      $marginLeft={`${Dimensions.units(0.5)}px`}
      $lineHeight={`${Dimensions.units(2)}px`}
    >
      <QuestionCircleIcon
        size="small"
        color={token('color.icon.subtle', '#626F86')}
        label="sen-tooltip"
      />
    </StyledSpanWithLineHeight>
  </Tooltip>
);

const NonDefaultStatusTooltip = () => (
  <Tooltip
    direction="up-middle"
    content={
      <StyledParagraphWithTextAlign $textAlign="center">
        <FormattedMessage {...messages.nonDefaultOrderStatusTooltip} />
      </StyledParagraphWithTextAlign>
    }
  >
    <StyledSpanWithLineHeight
      $marginLeft={`${Dimensions.units(0.5)}`}
      $lineHeight={`${Dimensions.units(2)}px`}
    >
      <QuestionCircleIcon
        size="small"
        color={token('color.icon.subtle', '#626F86')}
        label="sen-tooltip"
      />
    </StyledSpanWithLineHeight>
  </Tooltip>
);

const Link = ({ link }) => {
  if (link.href) {
    return (
      <AnchorLink href={link.href} data-testid={link.testId}>
        {link.label}
      </AnchorLink>
    );
  }
  return (
    <ButtonLink disabled={link.disabled} onClick={link.onClick} data-testid={link.testId}>
      {link.label}
    </ButtonLink>
  );
};

const strong = (chunks) => <StyledBoldStrong>{chunks}</StyledBoldStrong>;
const br = () => <br />;

const ActionLink = ({
  link,
  notCloneable,
  notEditable,
}: {
  link: OrdersActionLinkType;
  notCloneable?: boolean;
  notEditable?: boolean;
}) => {
  if (notEditable && link.testId === 'editQuote')
    return (
      <Tooltip
        direction="up"
        content={t('mac.orders.table.table-item.action.edit.disabled', {
          strong,
          br,
        })}
      >
        <Link link={link} />
      </Tooltip>
    );

  if (notCloneable && link.testId === 'cloneLink') {
    return (
      <Tooltip direction="up" content={t(messages.disableCloneQuoteHint)}>
        <Link link={link} />
      </Tooltip>
    );
  }

  return <Link link={link} />;
};

export const OrderCollapsedRow = ({
  showContent,
  rowItem,
  isPartner,
  urlOrderId,
  hash,
}: {
  showContent: boolean;
  rowItem: any;
  isPartner?: boolean;
  urlOrderId?: string;
  hash?: string;
}) => {
  const { cartOrderDetails, orderNumber, id } = rowItem;
  const dispatch = useDispatch();

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);

  const editCartUuid = useSelector(selectCopiedCartUUIDForEditing);
  const clonedOrder = useSelector((state: State) => selectClonedOrderResponse(id, state));

  const createdDate = cartOrderDetails?.created ?? '';

  useEffect(() => {
    if (clonedOrder) {
      dispatch(fetchQuotesPost(10, 0));
      dispatch({ type: CART_CLEAR_CLONE_QUOTE });
    }
  }, [clonedOrder]);

  const handleToggleDeleteModal = (e) => {
    e.stopPropagation();
    setDeleteModalIsOpen(!deleteModalIsOpen);
  };

  const handleJSONDownload = (e, orderId) => {
    e.stopPropagation();
    dispatch(fetchOrderJSON(orderId));
  };

  const handleInvoiceDownload = (e, cartId) => {
    e.stopPropagation();
    traceUFOPress(ORDER_INVOICE_ACTION_PDF_UFO_NAME);
    dispatch(fetchCartInvoicePDF(cartId));
  };

  const handlePDFDownload = (e, orderId) => {
    e.stopPropagation();
    traceUFOPress(ORDER_INVOICE_ACTION_PDF_UFO_NAME);
    dispatch(fetchOrderPDF(orderId));
  };

  const handleUpdatePONumber = (e, quoteDetails) => {
    e.stopPropagation();
    dispatch(openPONumberUpdateModal(quoteDetails));
  };

  const handleCloneQuote = (e, cartId) => {
    e.stopPropagation();

    dispatch(cloneQuote(cartId));
  };

  const handleEditQuote = (e, uuid) => {
    e.stopPropagation();
    dispatch(copyCartForEditing(uuid));
  };

  useEffect(() => {
    if (editCartUuid) {
      window.location.href = `${config.root}/checkout/review?purchaseMode=quote&uuid=${editCartUuid}`;
    }
  }, [editCartUuid]);

  const links = getOrderActions(
    rowItem,
    handleToggleDeleteModal,
    handleJSONDownload,
    handleInvoiceDownload,
    handlePDFDownload,
    handleUpdatePONumber,
    handleCloneQuote,
    handleEditQuote,
    isPartner,
    urlOrderId,
    hash,
  );

  const orderStatus = getOrderStatus(rowItem, isPartner);
  const isReviewRequired = rowItem.status === 'REVIEW_REQUIRED';
  const isNonDefaultOrderStatus =
    ['IN_PROGRESS', 'QUOTED'].indexOf(rowItem.status) === -1 && !isReviewRequired;

  return (
    <>
      <Cell width={15} key={`orders-row-sen-${orderNumber}`}>
        <ChevronCellWrapper>
          {showContent ? <ChevronUpIcon label="" /> : <ChevronDownIcon label="" />}
          <OrderNumberWrapper>{orderNumber}</OrderNumberWrapper>
        </ChevronCellWrapper>
      </Cell>
      <Cell width={15} key={`orders-row-created-${orderNumber}`}>
        <FormattedDate
          value={removeTimezone(createdDate)}
          year="numeric"
          month="short"
          day="2-digit"
        />
      </Cell>
      <Cell width={15} key={`orders-row-total-${orderNumber}`}>
        <AmountWithCurrency
          amount={cartOrderDetails?.totalIncTax}
          currency={cartOrderDetails?.currency || 'USD'}
          weight="400"
        />
      </Cell>
      <Cell
        width={15}
        key={`orders-row-status-${orderNumber}`}
        $color={isReviewRequired ? token('color.icon.danger', '#E34935') : 'inherit'}
      >
        {orderStatus}
        {isReviewRequired ? <ReviewRequiredTooltip /> : null}
        {isNonDefaultOrderStatus ? <NonDefaultStatusTooltip /> : null}
      </Cell>
      <OrdersActionsCell width={40} key={`orders-row-actions-${orderNumber}`}>
        {links.map((link) => (
          <ActionLink
            key={link.testId ?? (typeof link.label === 'string' ? link.label : link.href)}
            link={link}
            notCloneable={!rowItem.cloneable}
            notEditable={!rowItem.isEditableByCustomer}
          />
        ))}
      </OrdersActionsCell>
      <ModalTransition>
        {deleteModalIsOpen && (
          <DeleteQuoteModal rowItem={rowItem} onCancel={handleToggleDeleteModal} />
        )}
      </ModalTransition>
    </>
  );
};
